import {
  faFileAlt,
  faFileCode,
  faFileExcel,
  faFilePdf,
  faFileWord
} from '@fortawesome/free-solid-svg-icons';
import { CSSProperties, useState } from 'react';

export const availableExportFormats = [
  'odt',
  'txt',
  'html',
  'pdf',
  'ods',
  'csv',
  'xls',
  'doc',
  'rtf',
  'json'
] as const;

export type ExportFormat = typeof availableExportFormats[number];

const iconsByFormat = {
  odt: faFileAlt,
  txt: faFileAlt,
  html: faFileCode,
  pdf: faFilePdf,
  ods: faFileExcel,
  csv: faFileExcel,
  xls: faFileExcel,
  doc: faFileWord,
  rtf: faFileAlt,
  json: faFileCode
};

const getButtonStyle = (isExpanded: boolean): CSSProperties => ({
  alignItems: 'center',
  backgroundColor: isExpanded ? 'var(--background-primary)' : undefined,
  borderColor: isExpanded ? 'var(--background-primary)' : undefined,
  color: isExpanded ? '#fff' : '#616161',
  display: 'inline-flex',
  justifyContent: isExpanded ? 'flex-start' : 'center',
  maxWidth: isExpanded ? '90px' : '30px',
  minWidth: isExpanded ? '90px' : '30px',
  overflow: 'hidden',
  padding: '4px 6px',
  transition:
    'width 200ms linear, min-width 200ms linear, max-width 200ms linear, background-color 200ms linear, border-color 200ms linear',
  whiteSpace: 'nowrap',
  width: isExpanded ? '90px' : '30px'
});

type ExportOptionIconProps = {
  color: string;
  icon: {
    icon: [number, number, string[], string, string | string[]];
  };
};

const ExportOptionIcon: React.FC<ExportOptionIconProps> = ({ color, icon }) => {
  const [width, height, , , pathData] = icon.icon;
  const paths = Array.isArray(pathData) ? pathData : [pathData];

  return (
    <svg
      aria-hidden="true"
      className="export-option-icon"
      focusable="false"
      height="1em"
      style={{ flex: '0 0 auto', width: '1em' }}
      viewBox={`0 0 ${width} ${height}`}
      width="1em"
    >
      {paths.map((path, index) => (
        <path d={path} fill={color} key={index} style={{ fill: color }} />
      ))}
    </svg>
  );
};

export type ExportOptionProps = {
  format: ExportFormat;
  onExport: () => void;
  startSelected?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
};

export const ExportOption: React.FC<ExportOptionProps> = ({
  format,
  isSelected = false,
  onExport,
  onSelect
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const formatLabel = format.toUpperCase();
  const showFormat = isSelected || isFocused || isHovered;

  return (
    <button
      aria-label={`Exportar ${formatLabel}`}
      className={`btn export-option${
        showFormat ? ' export-option-expanded' : ''
      }`}
      onBlur={() => setIsFocused(false)}
      onClick={() => {
        onSelect?.();
        onExport();
      }}
      onFocus={() => setIsFocused(true)}
      onMouseEnter={() => {
        setIsHovered(true);
        onSelect?.();
      }}
      onMouseLeave={() => setIsHovered(false)}
      style={getButtonStyle(showFormat)}
      type="button"
    >
      <ExportOptionIcon
        color={showFormat ? '#fff' : '#616161'}
        icon={iconsByFormat[format]}
      />
      {showFormat && (
        <span
          style={{ color: '#fff', marginLeft: '5px', whiteSpace: 'nowrap' }}
        >
          {formatLabel}
        </span>
      )}
    </button>
  );
};
